import axios from 'axios'
import { auth } from '@/boot/firebase'

// The base API URL from environment (e.g. https://api.ninos-sv.org)
// Used for user-level endpoints like /my-enrollments that don't need a tenant domain
const BASE_API_URL = import.meta.env.VITE_API_BASE_URL || ''

// Create axios instance with default configuration
const api = axios.create({
  baseURL: BASE_API_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ─── Domain Management ─────────────────────────────────────────────────────

/**
 * Set the tenant domain for API calls.
 * Called after enrollment selection sets the domain (e.g. "https://tenant.ninos-sv.org").
 */
function setDomain(domain: string): void {
  localStorage.setItem('domain', domain)
  console.log('[Axios] Domain set to:', domain)
}

/**
 * Clear the tenant domain (on logout or enrollment switch).
 */
function clearDomain(): void {
  localStorage.removeItem('domain')
  console.log('[Axios] Domain cleared')
}

/**
 * Get the base API URL (non-tenant-specific).
 * Used for endpoints like /my-enrollments that work on any domain.
 */
function getBaseApiUrl(): string {
  return BASE_API_URL
}

// ─── Request Interceptor ───────────────────────────────────────────────────

api.interceptors.request.use(
  (config) => {
    // Add auth token
    const token = localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Set baseURL to tenant domain if available (unless already overridden by caller)
    // Callers can override by passing { baseURL: ... } in the request config
    if (!config.baseURL || config.baseURL === BASE_API_URL) {
      const domain = localStorage.getItem('domain')
      if (domain) {
        config.baseURL = domain
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// ─── Response Interceptor ──────────────────────────────────────────────────

api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      originalRequest._retryCount = originalRequest._retryCount || 0

      // Only retry up to 3 times
      if (originalRequest._retryCount < 3) {
        originalRequest._retryCount += 1

        try {
          // Get current Firebase user
          const currentUser = await auth.getCurrentUser()

          if (currentUser) {
            // Force refresh the token
            const newToken = await currentUser.getIdToken(true)

            // Update localStorage with new token
            localStorage.setItem('authToken', newToken)

            // Update the authorization header for the retry
            originalRequest.headers.Authorization = `Bearer ${newToken}`

            // Retry the original request
            return api(originalRequest)
          } else {
            // No current user, redirect to login
            throw new Error('No authenticated user')
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError)
          // Continue to logout if refresh fails
        }
      }

      // If we've retried 3 times or refresh failed, logout
      localStorage.removeItem('authToken')
      localStorage.removeItem('isAuthenticated')
      localStorage.removeItem('userId')
      localStorage.removeItem('userEmail')
      localStorage.removeItem('userName')
      localStorage.removeItem('domain')
      localStorage.removeItem('selected_enrollment')
      localStorage.removeItem('selected_enrollment_mode')
      window.location.href = '/login'
      return Promise.reject(error)
    }

    if (error.response?.status === 403) {
      console.error('Access forbidden')
    }

    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data?.message || 'Internal server error')
    }

    return Promise.reject(error)
  }
)

export { api, setDomain, clearDomain, getBaseApiUrl }
